import { useCallback } from 'react';
import { useKioskCheckin } from './useKioskCheckin';
import {
  KioskApptSearchParams,
  useKioskFindAppointment,
} from './useKioskFindAppointment';
import { useSite } from 'context/SiteContext';
import { useMutateUpdateKioskUser } from 'query/public';
import { isTelephonePatientFromFullName } from 'utils/user';
import { formatMonthDay } from 'utils/dateUtil';
import { useSubjectListener } from 'utils/subject';
import {
  KIOSK_APPT_SELECT,
  KIOSK_BIRTH_MONTH_INPUT,
  KIOSK_CHECKIN_LOADING,
  KIOSK_CHECKIN_SUCCESS,
  KIOSK_GENERAL_ERROR,
  KIOSK_MULTIPLE_RESULTS_ERROR,
  KIOSK_UPDATE_USER,
  KIOSK_UPDATE_USER_LOADING,
  KIOSK_PHONE_INPUT,
  SEARCHING_BIRTHDATE,
  SEARCHING_BIRTHDATE_SURNAME_INITIAL,
  SEARCHING_PHONE,
  KioskBirthdate,
  KioskState,
  kioskBirthDayInput,
  kioskSurnameInitialInput,
  useKioskState,
} from './useKioskState';

/**
 * In kiosk they are encouraged to write name into one input field and expected to write only firstname.
 * However in order to be well seen on patient screen this gets filled into surname and we fill the firstname with
 * placeholder just to keep both fields filled.
 */
const KIOSK_UPDATED_USER_FIRSTNAME_PLACEHOLDER = '-';

export type KioskInputType = 'BIRTHDATE' | 'PHONE';

export const useKiosk = () => {
  const { activeSite } = useSite();
  const {
    kioskState,
    setKioskState,
    reset,
    resetEvent,
    kioskApptSearchInput,
    setKioskApptSearchInput,
    appt,
    setAppt,
    onUserActivity,
  } = useKioskState();

  const { findAppointment, cancelFindAppointment } = useKioskFindAppointment();

  useSubjectListener(resetEvent, cancelFindAppointment, [
    cancelFindAppointment,
  ]);

  const search = useCallback(
    (
      params: KioskApptSearchParams,
      searchingState: KioskState,
      onMultipleResults?: () => void,
    ) => {
      setKioskState(searchingState);
      setKioskApptSearchInput(params);
      findAppointment(params, {
        onError: (errorType) => {
          if (errorType === KIOSK_MULTIPLE_RESULTS_ERROR && onMultipleResults) {
            onMultipleResults();
          } else {
            setKioskState(errorType);
          }
        },
        onSuccess: (appt) => {
          setAppt(appt);
          setKioskState(KIOSK_APPT_SELECT);
        },
      });
      onUserActivity();
    },
    [
      setKioskState,
      setKioskApptSearchInput,
      findAppointment,
      setAppt,
      onUserActivity,
    ],
  );

  const { checkin } = useKioskCheckin({
    onError: (errorType) => {
      setKioskState(errorType);
    },
    onSuccess: () => {
      setKioskState(KIOSK_CHECKIN_SUCCESS);
    },
  });

  const appointmentCheckIn = useCallback(() => {
    if (!appt) {
      setKioskState(KIOSK_GENERAL_ERROR);
      return;
    }
    setKioskState(KIOSK_CHECKIN_LOADING);
    checkin({
      siteId: activeSite.id,
      apptId: appt.id,
    });
  }, [activeSite, setKioskState, checkin, appt]);

  const confirmAppt = useCallback(() => {
    if (appt && isTelephonePatientFromFullName(appt?.user_full_name)) {
      setKioskState(KIOSK_UPDATE_USER);
    } else {
      setKioskState(KIOSK_CHECKIN_LOADING);
      appointmentCheckIn();
    }
    onUserActivity();
  }, [setKioskState, appointmentCheckIn, appt, onUserActivity]);

  const { mutateAsync: updateKioskUser } = useMutateUpdateKioskUser({
    onError: () => {
      setKioskState(KIOSK_GENERAL_ERROR);
    },
    onSuccess: () => {
      appointmentCheckIn();
    },
  });

  const updateUserAndCheckin = useCallback(
    (name: string) => {
      if (!appt) {
        setKioskState(KIOSK_GENERAL_ERROR);
        return;
      }
      setKioskState(KIOSK_UPDATE_USER_LOADING);
      updateKioskUser({
        site_id: activeSite.id,
        appt_id: appt.id,
        firstname: KIOSK_UPDATED_USER_FIRSTNAME_PLACEHOLDER,
        lastname: name,
      });
      onUserActivity();
    },
    [activeSite, appt, setKioskState, updateKioskUser, onUserActivity],
  );

  const setInputState = useCallback(
    (type: KioskInputType) => {
      setKioskState(
        type === 'BIRTHDATE' ? KIOSK_BIRTH_MONTH_INPUT : KIOSK_PHONE_INPUT,
      );
      onUserActivity();
    },
    [setKioskState, onUserActivity],
  );

  const showBirthDayInput = useCallback(
    (month: number) => {
      setKioskState(kioskBirthDayInput(month));
      onUserActivity();
    },
    [setKioskState, onUserActivity],
  );

  const selectBirthDay = useCallback(
    (birthdate: KioskBirthdate) => {
      search(
        { searchString: formatMonthDay(birthdate), type: 'BIRTHDATE' },
        SEARCHING_BIRTHDATE,
        () => setKioskState(kioskSurnameInitialInput(birthdate)),
      );
    },
    [search, setKioskState],
  );

  const searchByPhone = useCallback(
    (phone: string) => {
      search({ searchString: phone, type: 'PHONE' }, SEARCHING_PHONE);
    },
    [search],
  );

  const backToBirthMonth = useCallback(() => {
    setKioskState(KIOSK_BIRTH_MONTH_INPUT);
    onUserActivity();
  }, [setKioskState, onUserActivity]);

  const searchBySurnameInitial = useCallback(
    (birthdate: KioskBirthdate, surnameInitial: string) => {
      search(
        {
          searchString: `${formatMonthDay(birthdate)}_${surnameInitial}`,
          type: 'BIRTHDATE_SURNAME_INITIAL',
        },
        SEARCHING_BIRTHDATE_SURNAME_INITIAL,
      );
    },
    [search],
  );

  return {
    kioskState,
    showBirthDayInput,
    selectBirthDay,
    backToBirthMonth,
    searchBySurnameInitial,
    searchByPhone,
    appt,
    reset,
    resetEvent,
    setInputState,
    kioskApptSearchInput,
    onApptSelected: confirmAppt,
    updateUserAndCheckin,
    onUserActivity,
  };
};
